import * as vp from 'vopay';
import {
  Box,
  Tooltip,
  Pagination,
  Badge,
  CardTabs,
  formattedCurrency,
  capitalizeFirstLetter,
  GetColorStatus,
  usePagination,
  useUpdateQueryParams,
  MaterialSymbol,
} from 'vopay';
import { FunctionComponent, useState, useEffect, useMemo, JSX } from 'react';
import type { DefaultOptionProps } from 'vopay';
import { format, parseISO, subDays } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import ElinxHistorySearchForm from '@/components/Elinx/ElinxHistory/ElinxHistoryForm';
import useFetchELinxHistory from '@/hooks/useApi/useFetchELinxHistory';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useClientData } from '@/providers/ClientData.provider';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import ActivityType from '../ActivityType';
import type { ActivityTabType } from '../ActivityTab';
import { Button, TableFiltersContainer, CopyButton, TransactionStatus, Table } from 'UI';
import useFileDownload from '@/hooks/useFiledDownload';
import { useTranslation } from 'react-i18next';
import ElinxHistoryExport from '@/components/Elinx/ElinxHistory/ElinxHistoryExport';

type TabContents = {
  account: JSX.Element;
  payment: JSX.Element;
};

type QueryParams = {
  status?: string;
  searchTerm?: string;
  startDate?: string;
  endDate?: string;
  timeZone?: string;
  activityType?: ActivityTabType;
  eLinxType?: keyof TabContents;
};

type TransactionParams = {
  status: string;
  searchTerm: string;
  startDate: string;
  endDate: string;
  timeZone: string;
  hasInvalidEndDate: boolean;
  offset?: number;
};

type ElinxHistoryTableProps = {
  accountID: string;
  activityTypeOptions: DefaultOptionProps[];
  currentType: string;
  handleCurrentType: (val: ActivityTabType) => void;
};

const ElinxHistoryTable: FunctionComponent<ElinxHistoryTableProps> = ({
  accountID,
  activityTypeOptions,
  currentType,
  handleCurrentType,
}) => {
  const {
    downloadPdfTable,
    downloadCsvFile,
    downloadJson,
    downloadXls,
    downloadDoc,
    downloadTxt,
    downloadXml,
    printFile,
  } = useFileDownload();
  const {
    response: { clientAccountsEnabled },
  } = useClientData();
  const accountIDType = clientAccountsEnabled ? 'ClientAccountID' : 'ContactID';
  const { userSettings } = useUserSettingsProvider();
  const { t } = useTranslation('translations', { keyPrefix: 'ElinxHistory' });
  const [hasInvalidEndDate, setHasInvalidEndDate] = useState(false);
  const [shouldShowEndDateError, setShouldShowEndDateError] = useState(false);
  const [elinxHistory, , , getElinxHistory] = useFetchELinxHistory({});
  const [paylinkHistoryExport, , , fetchPaylinkHistoryExport] = useFetchELinxHistory({});
  const [filteredList, setFilteredList] = useState<vp.ELinxHistoryItem[]>([]);

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [activeTab, setActiveTab] = useState<keyof TabContents>(
    (queryParams.get('eLinxType') as keyof TabContents) || 'account',
  );
  const [columnSwap, setColumnSwap] = useState<'name' | 'email'>('name');
  const [idSwap, setIdSwap] = useState<'transaction' | 'schedule'>('transaction');

  const elinxRequestType: vp.ELinxRequestType[] =
    activeTab === 'account' ? ['beneficiary'] : ['scheduled', 'transaction', 'erp'];

  const timeZone = useMemo(
    () => (queryParams.get('timeZone') as vp.Timezone) || userSettings.timezone || 'UTC',
    [queryParams],
  );

  const initialStartDate = useMemo(() => formatInTimeZone(subDays(new Date(), 7), timeZone, 'yyyy-MM-dd'), [timeZone]);
  const initialEndDate = useMemo(() => formatInTimeZone(new Date(), timeZone, 'yyyy-MM-dd'), [timeZone]);

  const status = useMemo(() => queryParams.get('status') || '', [queryParams]);
  const searchTerm = useMemo(() => queryParams.get('searchTerm') || '', [queryParams]);
  const startDate = useMemo(() => queryParams.get('startDate') || initialStartDate || '', [queryParams]);
  const endDate = useMemo(() => queryParams.get('endDate') || initialEndDate || '', [queryParams]);

  const filtersApplied = useMemo(() => {
    return Boolean(
      status ||
        searchTerm ||
        (startDate && startDate !== initialStartDate) ||
        (endDate && endDate !== initialEndDate) ||
        (timeZone && timeZone !== (userSettings.timezone || 'UTC')),
    );
  }, [status, searchTerm, startDate, endDate, timeZone, initialStartDate, initialEndDate, userSettings.timezone]);
  const alertDialog = useAlertDialog();

  const updateQueryParams = useUpdateQueryParams<QueryParams>();

  const handleTabChange = (value: string) => {
    if (value === 'account' || value === 'payment') {
      setActiveTab(value);
    }
  };

  // pagination
  const PER_PAGE = 20;
  const totalDataLength = useMemo(() => {
    return elinxHistory.PaginationMetaData?.TotalRecords || 0;
  }, [elinxHistory.ELinxRequests]);

  const { pageCount, pageIndex, setPageIndex } = usePagination(totalDataLength, {
    pageSize: PER_PAGE,
  });

  const fetchElinxHistory = async ({
    startDate,
    endDate,
    status,
    hasInvalidEndDate,
    searchTerm,
    timeZone,
    offset = 0,
  }: TransactionParams) => {
    const elinxHistoryRequest = {
      StartDateTime: startDate,
      EndDateTime: endDate,
      Timezone: timeZone,
      ELinxStatus: status,
      ELinxRequestType: elinxRequestType,
      SearchText: searchTerm,
      Limit: PER_PAGE,
      Offset: offset,
      [accountIDType]: accountID,
    };

    if (hasInvalidEndDate) {
      setShouldShowEndDateError(true);
      return;
    }
    try {
      const res = await getElinxHistory(elinxHistoryRequest);

      if (!res.Success) throw new Error(res.ErrorMessage);
      const ELinxRequests = Object.values(res.ELinxRequests || {});
      setFilteredList(ELinxRequests);
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('ErrorMessages.fetchingError');
      alertDialog.error(t('ErrorMessages.error'), msg);
    }
  };

  useEffect(() => {
    setHasInvalidEndDate(startDate > endDate);
  }, [startDate, endDate]);

  useEffect(() => {
    const searchParams = {
      status,
      searchTerm,
      startDate: startDate || initialStartDate,
      endDate: endDate || initialEndDate,
      timeZone: timeZone,
    };

    updateQueryParams({ ...searchParams, activityType: 'eLinx History', eLinxType: activeTab });
    const offset = setPageIndex(0);

    fetchElinxHistory({
      startDate,
      endDate,
      status,
      hasInvalidEndDate: false,
      searchTerm,
      timeZone,
      offset,
    });
  }, [activeTab]);

  const handleExport = async (exportType: 'pdf' | 'csv' | 'xls' | 'doc' | 'text' | 'json' | 'xml' | 'print') => {
    const defaultValues = {
      StartDateTime: initialStartDate,
      EndDateTime: initialEndDate,
      Timezone: timeZone,
      ELinxStatus: status,
      ELinxRequestType: elinxRequestType,
      SearchText: searchTerm,
    };
    const elinxHistoryRequest = defaultValues;
    elinxHistoryRequest.StartDateTime = startDate;
    elinxHistoryRequest.EndDateTime = endDate;
    const exportElinxData = await fetchPaylinkHistoryExport(elinxHistoryRequest);
    switch (exportType) {
      case 'pdf':
        downloadPdfTable('export-elinx-history-table', t('title'));
        break;
      case 'csv':
        downloadCsvFile('export-elinx-history-table', t('title'));
        break;
      case 'xls':
        downloadXls('export-elinx-history-table', t('title'));
        break;
      case 'doc':
        downloadDoc('export-elinx-history-table', t('title'));
        break;
      case 'text':
        downloadTxt('export-elinx-history-table', t('title'));
        break;
      case 'json':
        downloadJson(Object.values(exportElinxData.ELinxRequests || {}), t('title'));
        break;
      case 'xml':
        downloadXml('export-elinx-history-table', t('title'));
        break;
      case 'print':
        printFile('export-elinx-history-table');
        break;
      default:
        break;
    }
  };

  const tableProps: vp.TableProps<vp.ELinxHistoryItem> = {
    id: 'elinx-history-table',
    version: '2',
    zebra: true,
    columns: [
      {
        id: 'creationDate',
        header: t('Columns.creationDate'),
        align: 'left',
        field: 'RequestDate',
      },
      {
        id: 'eLinxID',
        header: t('Columns.elinxId'),
        align: 'left',
        template: ({ data }) => (
          <Box gap="sm" ai="center">
            <Table.Link
              to={{
                pathname: `/elinx-history/details/${data.ELinxRequestID}`,
                state: { eLinx: data },
              }}
            >
              {data.ELinxRequestID.toString()}
            </Table.Link>
            <CopyButton content={data.ELinxRequestID.toString()} color="rgba(45, 45, 45, 0.6)" />
          </Box>
        ),
      },
      {
        id: 'eLinxType',
        header: t('Columns.elinxType'),
        align: 'left',
        visible: activeTab === 'payment',
        template: ({ data }) =>
          data.PaymentType === 'withdraw' ? 'ePay' : data.PaymentType === 'fund' ? 'eFlex' : t('notApplicable'),
      },
      {
        id: 'requestStatus',
        header: t('Columns.requestStatus'),
        align: 'left',
        template: ({ data }) => (
          <Box flx="row" ai="center">
            <Box flx="row" ai="center">
              <TransactionStatus status={GetColorStatus(data.Status)} label={capitalizeFirstLetter(data.Status)} />
            </Box>
            {activeTab === 'payment' && data.Status === 'completed' && (
              <Box ps="sm">
                <Tooltip
                  type="hover"
                  information={t('TooltipMessages.requestCompleted')}
                  position="top"
                  hasArrow
                  icon="info"
                />
              </Box>
            )}
          </Box>
        ),
      },
      {
        id: 'recipientName',
        header: (
          <Box gap="sm" ai="center">
            {t('Columns.recipientName')}
            <MaterialSymbol icon={'switch_left'} fillAmount={1} size={'24px'} />
          </Box>
        ),
        align: 'left',
        visible: columnSwap === 'name',
        field: 'ReceiverName',
        onHeaderClick: () => {
          setColumnSwap('email');
        },
      },
      {
        id: 'recipientEmail',
        header: (
          <Box gap="sm" ai="center">
            {t('Columns.recipientEmail')}
            <MaterialSymbol icon={'switch_right'} fillAmount={1} size={'24px'} />
          </Box>
        ),
        align: 'left',
        visible: columnSwap === 'email',
        field: 'ReceiverEmailAddress',
        onHeaderClick: () => {
          setColumnSwap('name');
        },
      },
      {
        id: 'transactionID',
        header: (
          <Box gap="sm" ai="center">
            {t('Columns.transactionId')}
            <MaterialSymbol icon={'switch_left'} fillAmount={1} size={'24px'} />
          </Box>
        ),
        align: 'left',
        visible: activeTab === 'payment' && idSwap === 'transaction',
        template: ({ data }) => data.TransactionID?.toString() || t('notApplicable'),
        onHeaderClick: () => {
          setIdSwap('schedule');
        },
      },
      {
        id: 'scheduleID',
        header: (
          <Box gap="sm" ai="center">
            {t('Columns.scheduleId')}
            <MaterialSymbol icon={'switch_right'} fillAmount={1} size={'24px'} />
          </Box>
        ),
        align: 'left',
        visible: activeTab === 'payment' && idSwap === 'schedule',
        template: ({ data }) => data.ScheduledPaymentsID?.toString() || t('notApplicable'),
        onHeaderClick: () => {
          setIdSwap('transaction');
        },
      },
      {
        id: 'account',
        header: t('Columns.account'),
        align: 'left',
        template: ({ data }) =>
          data.Status === 'completed' ? (
            <>
              {data.AccountNumber && <span>{vp.getMaskedNumber(data.AccountNumber)}</span>}
              {data.CreditCardNumber && <span>{vp.getMaskedNumber(String(data.CreditCardNumber))}</span>}
            </>
          ) : (
            t('notApplicable')
          ),
      },
      {
        id: 'accountType',
        header: t('Columns.accountType'),
        align: 'left',
        visible: activeTab === 'account',
        template: ({ data }) =>
          data.Status === 'completed' ? (
            <>
              {data.AccountNumber && t('bankAccount')}
              {data.CreditCardNumber && t('creditCard')}
            </>
          ) : (
            t('notApplicable')
          ),
      },
      {
        id: 'amount',
        header: t('Columns.amount'),
        align: 'left',
        visible: activeTab === 'payment',
        template: ({ data }) => `$${formattedCurrency(String(data.Amount))}`,
      },
      {
        id: 'view',
        header: '',
        align: 'right',
        template: ({ data }) => (
          <Box gap="sm" jc="fe">
            {data.Status === 'pending' && (
              <Button
                id={`copy-url-${data.TransactionID}`}
                size="small"
                onClick={() => {
                  void navigator.clipboard.writeText(data.Link);
                }}
              >
                {t('copyUrl')}
              </Button>
            )}
          </Box>
        ),
      },
    ],
    rows: filteredList.map((item): { id: string; data: vp.ELinxHistoryItem } => ({
      id: String(item.ELinxRequestID || ''),
      data: item,
    })),
  };

  const exportColumns = tableProps.columns
    .filter((col) => col.id !== 'view')
    .map((col) => {
      if (col.id === 'requestStatus') {
        const { field, ...rest } = col;
        return {
          ...rest,
          template: ({ data }: { data: vp.ELinxHistoryItem }) => (
            <Box flx="row" ai="center">
              <Badge label={capitalizeFirstLetter(data.Status)} color={GetColorStatus(data.Status)} />
            </Box>
          ),
        };
      }
      return col;
    });

  const tableExportProps: vp.TableProps<vp.ELinxHistoryItem> = {
    ...tableProps,
    id: 'export-elinx-history-table',
    columns: exportColumns,
    rows: Object.values(paylinkHistoryExport.ELinxRequests || {}).map(
      (item): { id: string; data: vp.ELinxHistoryItem } => ({
        id: String(item.ELinxRequestID || ''),
        data: item,
      }),
    ),
  };

  return (
    <Box flx="col" gap="md">
      <Box ai="center" jc="sb">
        <ActivityType
          activityTypeOptions={activityTypeOptions}
          currentType={currentType}
          handleCurrentType={handleCurrentType}
        />
        <ElinxHistoryExport onExport={handleExport} />
      </Box>

      <CardTabs.Root value={activeTab} onValueChange={handleTabChange}>
        <CardTabs.List aria-label="Elinx History">
          <CardTabs.Trigger value="account">
            <span>{t('Tabs.account')}</span>
          </CardTabs.Trigger>
          <CardTabs.Trigger value="payment">
            <span>{t('Tabs.payment')}</span>
          </CardTabs.Trigger>
        </CardTabs.List>
        {['account', 'payment'].map((tab) => (
          <CardTabs.Content key={tab} value={tab}>
            <Box flx="col" gap="sm">
              <TableFiltersContainer filtersApplied={filtersApplied}>
                <Box ai="center" jc="fe">
                  <TableFiltersContainer.Trigger />
                </Box>
                <TableFiltersContainer.Content>
                  <ElinxHistorySearchForm
                    requestStatus={status}
                    timeZone={timeZone}
                    searchTerm={searchTerm}
                    hasInvalidEndDate={hasInvalidEndDate}
                    shouldShowEndDateError={shouldShowEndDateError}
                    setShouldShowEndDateError={setShouldShowEndDateError}
                    initialTimeRange={[
                      parseISO(startDate).getTime() as vp.Timestamp,
                      parseISO(endDate).getTime() as vp.Timestamp,
                    ]}
                    search={(searchParams) => {
                      updateQueryParams({
                        status: searchParams.requestStatus,
                        searchTerm: searchParams.searchTerm,
                        startDate: format(new Date(searchParams.timeRange[0]), 'yyyy-MM-dd'),
                        endDate: format(new Date(searchParams.timeRange[1]), 'yyyy-MM-dd'),
                        timeZone: searchParams.timeZone,
                      });

                      fetchElinxHistory({
                        startDate: format(new Date(searchParams.timeRange[0]), 'yyyy-MM-dd'),
                        endDate: format(new Date(searchParams.timeRange[1]), 'yyyy-MM-dd'),
                        status: searchParams.requestStatus,
                        hasInvalidEndDate: searchParams.timeRange[0] > searchParams.timeRange[1],
                        searchTerm: searchParams.searchTerm,
                        timeZone: searchParams.timeZone,
                      });
                    }}
                  />
                </TableFiltersContainer.Content>
              </TableFiltersContainer>
            </Box>
            <div style={{ display: 'none' }}>
              <Table {...tableExportProps} />
            </div>
            <Box>
              <Table {...tableProps} />
            </Box>
            {tableProps.rows && tableProps.rows.length === 0 && (
              <Box flx="col" jc="center">
                <Box ai="center" jc="center" pt="md">
                  {t('noRecordMessage')}
                </Box>
              </Box>
            )}
            <Box width="100%" jc="center" mt="lg" mb="sm">
              {pageCount > 1 && (
                <Pagination
                  pageCount={pageCount}
                  forcePageNumber={pageIndex}
                  handlePageClick={(e) => {
                    const offset = setPageIndex(e.selected);
                    fetchElinxHistory({
                      startDate,
                      endDate,
                      status,
                      hasInvalidEndDate: startDate > endDate,
                      searchTerm,
                      timeZone,
                      offset,
                    });
                  }}
                />
              )}
            </Box>
          </CardTabs.Content>
        ))}
      </CardTabs.Root>
    </Box>
  );
};
export default ElinxHistoryTable;
